import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { axios, useAuth } from '../../context/AuthContext';
import yedkursLogo from '../../images/yedkursLogo.png';
import {
  printPaymentReceiptDocument,
  makeReceiptNo,
} from '../../services/contractDocuments';
import {
  buildSelectedInstallmentPaymentPreview,
  reconcileInstallmentPayments,
  resolvePaymentPostingInstallmentId,
} from '../../services/installmentAccounting';

const TL = value => new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(Number(value || 0));

const formatDate = value => {
  if (!value) return '—';
  const text = String(value).slice(0, 10);
  const [year, month, day] = text.split('-');
  return year && month && day ? `${day}.${month}.${year}` : '—';
};

const extractList = payload => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const STATUS = {
  aktif: { label: 'Aktif', color: '#10B981' },
  tamamlandi: { label: 'Tamamlandı', color: '#3B82F6' },
  donduruldu: { label: 'Donduruldu', color: '#F59E0B' },
  iptal: { label: 'İptal', color: '#94A3B8' },
  odendi: { label: 'Ödendi', color: '#10B981' },
  kismi: { label: 'Kısmi ödendi', color: '#F59E0B' },
  bekliyor: { label: 'Bekliyor', color: '#3B82F6' },
  gecikti: { label: 'Gecikmiş', color: '#EF4444' },
};

const paymentMethod = value => ({
  nakit: 'Nakit',
  kredi_karti: 'Kredi Kartı',
  banka_havalesi: 'Banka Havalesi',
  senet: 'Senet',
  pos: 'POS',
  diger: 'Diğer',
}[value] || value || '—');

const getLocalDate = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};

const remainingAmount = installment => Math.max(0, Number(
  installment?.kalan_tutar ?? (Number(installment?.tutar || 0) - Number(installment?.odeme_tutari || 0))
));

const buildPaymentAllocations = (installments, selected, value) => {
  return buildSelectedInstallmentPaymentPreview(
    installments,
    value,
    selected?.sozlesme_id,
    selected?.id
  );
};

function CollectionModal({ installment, installments, studentName, onSave, onClose }) {
  const [form, setForm] = useState({
    tutar: remainingAmount(installment) || '',
    odeme_yontemi: 'nakit',
    odeme_tarihi: getLocalDate(),
    makbuz_no: makeReceiptNo(installment),
    notlar: '',
  });
  const [saving, setSaving] = useState(false);
  const preview = buildPaymentAllocations(installments, installment, form.tutar);

  const submit = async () => {
    if (Number(form.tutar) <= 0) return toast.error('0’dan büyük bir tahsilat tutarı giriniz');
    if (preview.extra > 0) return toast.error(`En fazla ${TL(preview.totalRemaining)} tahsil edilebilir`);
    const ilkAcikTaksitId = resolvePaymentPostingInstallmentId(preview);
    if (!ilkAcikTaksitId) return toast.error('Ödenecek açık taksit bulunamadı');
    setSaving(true);
    try {
      // Eski API sürümleri dağıtıma gönderilen taksitten başlıyordu. Her iki
      // sürümde de önizlemeyle aynı sonucu almak için isteği ilk açık taksitle başlat.
      await onSave({ ...form, taksit_id: ilkAcikTaksitId });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth:500 }} onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">💳 Tahsilat Al</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ background:'var(--bg-elevated)', borderRadius:12, padding:'12px 16px', marginBottom:16 }}>
          <div style={{ fontWeight:800 }}>{studentName} · Taksit #{installment.taksit_no}</div>
          <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginTop:4 }}>
            Vade: {formatDate(installment.vade_tarihi)} · Taksit kalanı: {TL(remainingAmount(installment))}
          </div>
        </div>

        <div style={{ background:'#EFF6FF', color:'#1E40AF', border:'1px solid #BFDBFE', borderRadius:10, padding:10, marginBottom:14, fontSize:'0.78rem', fontWeight:650 }}>
          Önce seçtiğiniz taksitten önce kalan kısmi borç tamamlanır; ardından ödeme seçtiğiniz taksite işlenir.
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div className="form-group">
            <label className="form-label">Tahsilat tutarı (₺) *</label>
            <input className="form-input" type="number" min="0.01" step="0.01" value={form.tutar}
              onChange={event => setForm(current => ({ ...current, tutar:event.target.value }))} />
          </div>

          {preview.allocations.length > 0 && (
            <div style={{ padding:12, borderRadius:10, background:preview.extra > 0 ? '#FEF2F2' : '#ECFDF5', border:`1px solid ${preview.extra > 0 ? '#FCA5A5' : '#A7F3D0'}` }}>
              <div style={{ fontWeight:800, fontSize:'0.8rem' }}>Tahsilat dağılımı</div>
              {preview.allocations.map(item => (
                <div key={item.id} style={{ display:'flex', justifyContent:'space-between', gap:12, fontSize:'0.78rem', marginTop:6 }}>
                  <span>#{item.taksit_no} taksit</span>
                  <strong>{TL(item.applied)} → kalan {TL(item.after)}</strong>
                </div>
              ))}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Ödeme yöntemi</label>
            <select className="form-input" value={form.odeme_yontemi}
              onChange={event => setForm(current => ({ ...current, odeme_yontemi:event.target.value }))}>
              <option value="nakit">Nakit</option>
              <option value="kredi_karti">Kredi Kartı</option>
              <option value="banka_havalesi">Banka Havalesi</option>
              <option value="pos">POS</option>
            </select>
          </div>

          <div className="muh-grid-2">
            <div className="form-group">
              <label className="form-label">Ödeme tarihi</label>
              <input className="form-input" type="date" value={form.odeme_tarihi}
                onChange={event => setForm(current => ({ ...current, odeme_tarihi:event.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Makbuz / Fiş No</label>
              <input className="form-input" value={form.makbuz_no}
                onChange={event => setForm(current => ({ ...current, makbuz_no:event.target.value }))} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Not</label>
            <textarea className="form-input" rows={2} value={form.notlar}
              onChange={event => setForm(current => ({ ...current, notlar:event.target.value }))} />
          </div>
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>İptal</button>
          <button className="btn btn-primary" onClick={submit}
            disabled={saving || Number(form.tutar) <= 0 || preview.extra > 0}>
            {saving ? 'Kaydediliyor…' : 'Tahsilatı Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditCollectionModal({ payment, studentName, onSave, onClose }) {
  const [form, setForm] = useState({
    tutar: payment.tutar,
    odeme_yontemi: payment.odeme_yontemi || 'nakit',
    odeme_tarihi: String(payment.odeme_tarihi || '').slice(0, 10),
    makbuz_no: payment.makbuz_no || '',
    notlar: payment.notlar || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (Number(form.tutar) <= 0) return toast.error('0’dan büyük bir tahsilat tutarı giriniz');
    setSaving(true);
    try {
      await onSave(payment.id, form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth:500 }} onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">✏️ Tahsilatı Düzenle</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ background:'var(--bg-elevated)', borderRadius:12, padding:'12px 16px', marginBottom:16 }}>
          <div style={{ fontWeight:800 }}>{studentName}</div>
          <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginTop:4 }}>
            Taksit: {payment.taksitLabel} · Mevcut tutar: {TL(payment.tutar)}
          </div>
        </div>

        <div style={{ padding:11, marginBottom:14, borderRadius:10, background:'#FFF7ED', color:'#9A3412', fontSize:'0.78rem', fontWeight:650 }}>
          Tutar değiştirildiğinde taksit bakiyeleri ve kasa hareketi otomatik olarak yeniden hesaplanır.
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div className="form-group">
            <label className="form-label">Tahsilat tutarı (₺) *</label>
            <input className="form-input" type="number" min="0.01" step="0.01" value={form.tutar}
              onChange={event => setForm(current => ({ ...current, tutar:event.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Ödeme yöntemi</label>
            <select className="form-input" value={form.odeme_yontemi}
              onChange={event => setForm(current => ({ ...current, odeme_yontemi:event.target.value }))}>
              <option value="nakit">Nakit</option>
              <option value="kredi_karti">Kredi Kartı</option>
              <option value="banka_havalesi">Banka Havalesi</option>
              <option value="senet">Senet</option>
              <option value="pos">POS</option>
              <option value="diger">Diğer</option>
            </select>
          </div>
          <div className="muh-grid-2">
            <div className="form-group">
              <label className="form-label">Ödeme tarihi</label>
              <input className="form-input" type="date" value={form.odeme_tarihi}
                onChange={event => setForm(current => ({ ...current, odeme_tarihi:event.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Makbuz / Fiş No</label>
              <input className="form-input" value={form.makbuz_no}
                onChange={event => setForm(current => ({ ...current, makbuz_no:event.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Not</label>
            <textarea className="form-input" rows={3} value={form.notlar}
              onChange={event => setForm(current => ({ ...current, notlar:event.target.value }))} />
          </div>
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>İptal</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || Number(form.tutar) <= 0}>
            {saving ? 'Güncelleniyor…' : 'Tahsilatı Güncelle'}
          </button>
        </div>
      </div>
    </div>
  );
}

const SummaryCard = ({ label, value, color, hint }) => (
  <div className="card" style={{ padding:16, minWidth:0 }}>
    <div style={{ fontSize:'0.74rem', color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase' }}>{label}</div>
    <div style={{ fontFamily:'var(--font-ui)', fontSize:'1.35rem', fontWeight:900, color, marginTop:6 }}>{value}</div>
    {hint && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:4 }}>{hint}</div>}
  </div>
);

export default function SozlesmeDetayPage() {
  const { id } = useParams();
  const { user } = useAuth?.() || {};
  const navigate = useNavigate();
  const [sozlesme, setSozlesme] = useState(null);
  const [odemeler, setOdemeler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const [detayResponse, odemeResponse] = await Promise.all([
          axios.get(`/muhasebe/sozlesmeler/${id}`),
          axios.get(`/muhasebe/odemeler?sozlesme_id=${id}&limit=10000`)
            .catch(() => ({ data: { success: false, data: [] } })),
        ]);

        if (!active) return;
        if (!detayResponse.data?.success) throw new Error('Sözleşme bulunamadı');

        const detail = detayResponse.data.data;
        setSozlesme({
          ...detail,
          taksitler: reconcileInstallmentPayments(detail.taksitler || []),
        });
        setOdemeler(
          extractList(odemeResponse.data)
            .filter(payment => String(payment.sozlesme_id) === String(id))
        );
      } catch (error) {
        if (active) {
          toast.error(error.response?.data?.message || error.message || 'Sözleşme detayları yüklenemedi');
          setSozlesme(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [id, reloadKey]);

  const summary = useMemo(() => {
    const installments = (sozlesme?.taksitler || []).filter(item => item.durum !== 'iptal');
    const planned = installments.reduce((sum, item) => sum + Number(item.tutar || 0), 0);
    const paid = installments.reduce((sum, item) => sum + Number(item.odeme_tutari || 0), 0);
    const remaining = installments.reduce((sum, item) => sum + Number(item.kalan_tutar || 0), 0);
    const paidCount = installments.filter(item => Number(item.kalan_tutar || 0) <= 0).length;
    return {
      planned,
      paid,
      remaining,
      paidCount,
      count: installments.length,
      progress: planned > 0 ? Math.min(100, Math.round((paid / planned) * 100)) : 0,
    };
  }, [sozlesme]);

  const collections = useMemo(() => {
    const grouped = new Map();
    odemeler.forEach(payment => {
      const key = payment.tahsilat_grup_uuid || `payment-${payment.id}`;
      const current = grouped.get(key) || {
        ...payment,
        tutar: 0,
        taksitNolari: [],
      };
      current.tutar += Number(payment.tutar || 0);
      current.taksitNolari.push(Number(payment.taksit_no));
      grouped.set(key, current);
    });

    return [...grouped.values()].map(payment => {
      const taksitNolari = [...new Set(payment.taksitNolari)].sort((a, b) => a - b);
      return {
        ...payment,
        taksitNolari,
        taksitLabel: taksitNolari.map(number => `#${number}`).join(', '),
      };
    });
  }, [odemeler]);

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;

  if (!sozlesme) {
    return (
      <div className="empty-state">
        <h3>Sözleşme bulunamadı</h3>
        <button className="btn btn-outline" onClick={() => navigate('/muhasebe/sozlesmeler')}>Sözleşmelere dön</button>
      </div>
    );
  }

  const contractStatus = STATUS[sozlesme.durum] || { label: sozlesme.durum, color: '#94A3B8' };
  const studentName = `${sozlesme.ad || ''} ${sozlesme.soyad || ''}`.trim();
  const firstOpenInstallment = (sozlesme.taksitler || []).find(item => item.durum !== 'iptal' && remainingAmount(item) > 0);

  const saveCollection = async form => {
    try {
      await axios.post('/muhasebe/tahsilat', form);
      toast.success('Tahsilat kaydedildi');

      if (sozlesme) {
        const studentInfo = sozlesme.sozlesme_form_bilgileri || {
          ad: sozlesme.ad,
          soyad: sozlesme.soyad,
          veli_adi: sozlesme.veli_adi,
          veli_telefon: sozlesme.veli_tel,
          sinif_adi: sozlesme.sinif_adi,
          paket: sozlesme.paket,
          egitim_yili: sozlesme.egitim_yili,
        };
        printPaymentReceiptDocument({
          contract: sozlesme,
          student: studentInfo,
          payment: form,
          installments: sozlesme.taksitler || [],
          payments: [...(odemeler || []), form],
          cashierName: [user?.ad, user?.soyad].filter(Boolean).join(' '),
          logoUrl: yedkursLogo,
        });
      }

      setSelectedInstallment(null);
      setReloadKey(current => current + 1);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Tahsilat kaydedilemedi');
      throw error;
    }
  };

  const printCollectionReceipt = (payment) => {
    if (!sozlesme) return;
    const studentInfo = sozlesme.sozlesme_form_bilgileri || {
      ad: sozlesme.ad,
      soyad: sozlesme.soyad,
      veli_adi: sozlesme.veli_adi,
      veli_telefon: sozlesme.veli_tel,
      sinif_adi: sozlesme.sinif_adi,
      paket: sozlesme.paket,
      egitim_yili: sozlesme.egitim_yili,
    };
    const cashierName = payment.kaydeden_ad
      ? `${payment.kaydeden_ad} ${payment.kaydeden_soyad || ''}`.trim()
      : [user?.ad, user?.soyad].filter(Boolean).join(' ');

    const opened = printPaymentReceiptDocument({
      contract: sozlesme,
      student: studentInfo,
      payment,
      installments: sozlesme.taksitler || [],
      payments: odemeler || [],
      cashierName,
      logoUrl: yedkursLogo,
    });
    if (!opened) toast.error('Makbuz penceresi açılamadı. Tarayıcı açılır pencere (popup) iznini kontrol edin.');
  };

  const printInstallmentReceipt = (installment) => {
    if (!sozlesme) return;
    const matchingPayment = (odemeler || []).find(o => String(o.taksit_id) === String(installment.id));
    const payment = matchingPayment || {
      id: installment.id,
      taksit_id: installment.id,
      taksit_no: installment.taksit_no,
      vade_tarihi: installment.vade_tarihi,
      tutar: installment.odeme_tutari || installment.tutar,
      odeme_tarihi: installment.gercek_odeme || installment.odeme_tarihi || getLocalDate(),
      odeme_yontemi: installment.odeme_yontemi || 'nakit',
      makbuz_no: installment.makbuz_no || makeReceiptNo(installment),
      notlar: installment.notlar || '',
    };
    printCollectionReceipt(payment);
  };

  const printGeneralStatementReceipt = () => {
    if (!sozlesme) return;
    const studentInfo = sozlesme.sozlesme_form_bilgileri || {
      ad: sozlesme.ad,
      soyad: sozlesme.soyad,
      veli_adi: sozlesme.veli_adi,
      veli_telefon: sozlesme.veli_tel,
      sinif_adi: sozlesme.sinif_adi,
      paket: sozlesme.paket,
      egitim_yili: sozlesme.egitim_yili,
    };
    const totalPaid = (odemeler || []).reduce((sum, p) => sum + Number(p.tutar || 0), 0)
      || (sozlesme.taksitler || []).reduce((sum, t) => sum + Number(t.odeme_tutari || 0), 0);

    if (totalPaid <= 0) {
      toast.error('Bu sözleşmeye ait henüz tahsilat kaydı bulunmuyor.');
      return;
    }

    const generalPayment = {
      id: 'genel-ekstre',
      tutar: totalPaid,
      odeme_tarihi: getLocalDate(),
      odeme_yontemi: 'karma',
      makbuz_no: `EKSTRE-${sozlesme.id}-${getLocalDate().replace(/-/g, '')}`,
      notlar: 'Toplam Tahsilat ve Hesap Dökümü Ekstresi',
      isGeneralStatement: true,
    };

    const opened = printPaymentReceiptDocument({
      contract: sozlesme,
      student: studentInfo,
      payment: generalPayment,
      installments: sozlesme.taksitler || [],
      payments: odemeler || [],
      cashierName: [user?.ad, user?.soyad].filter(Boolean).join(' '),
      logoUrl: yedkursLogo,
    });
    if (!opened) toast.error('Makbuz penceresi açılamadı. Tarayıcı açılır pencere (popup) iznini kontrol edin.');
  };

  const updateCollection = async (paymentId, form) => {
    try {
      await axios.put(`/muhasebe/odemeler/${paymentId}`, form);
      toast.success('Tahsilat güncellendi');
      setSelectedPayment(null);
      setReloadKey(current => current + 1);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Tahsilat güncellenemedi');
      throw error;
    }
  };

  const deleteCollection = async payment => {
    const confirmed = window.confirm(
      `${payment.taksitLabel} taksitlerine ait ${TL(payment.tutar)} tutarındaki tahsilatı silmek istediğinize emin misiniz?\n\nKasa hareketi kaldırılacak ve taksit bakiyeleri yeniden hesaplanacaktır.`
    );
    if (!confirmed) return;

    setDeletingPaymentId(payment.id);
    try {
      await axios.delete(`/muhasebe/odemeler/${payment.id}`);
      toast.success('Tahsilat silindi; taksit bakiyeleri güncellendi');
      setSelectedPayment(null);
      setReloadKey(current => current + 1);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Tahsilat silinemedi');
    } finally {
      setDeletingPaymentId(null);
    }
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
      <div className="animate-fadeInUp" style={{ display:'flex', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/muhasebe/sozlesmeler')}>← Sözleşmeler</button>
        <div style={{ flex:1, minWidth:220 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <h2 style={{ fontFamily:'var(--font-ui)', fontWeight:900, margin:0 }}>{studentName || 'Sözleşme Detayı'}</h2>
            <span className="badge" style={{ background:`${contractStatus.color}22`, color:contractStatus.color }}>{contractStatus.label}</span>
          </div>
          <div style={{ color:'var(--text-muted)', fontSize:'0.84rem', marginTop:5 }}>
            Sözleşme #{sozlesme.id} · {sozlesme.paket || '—'} · {sozlesme.egitim_yili || '—'} · {sozlesme.sinif_adi || '—'}
          </div>
        </div>
        {firstOpenInstallment && (
          <button className="btn btn-primary" onClick={() => setSelectedInstallment(firstOpenInstallment)}>
            💳 Tahsilat Al
          </button>
        )}
      </div>

      <div className="muh-grid-4 animate-fadeInUp">
        <SummaryCard label="Net sözleşme" value={TL(sozlesme.net_ucret || summary.planned)} color="var(--blue)" hint={`Brüt ${TL(sozlesme.brut_ucret)}`} />
        <SummaryCard label="Toplam ödenen" value={TL(summary.paid)} color="#10B981" hint={`${summary.paidCount} / ${summary.count} taksit tamamlandı`} />
        <SummaryCard label="Toplam kalan" value={TL(summary.remaining)} color={summary.remaining > 0 ? '#EF4444' : '#10B981'} hint={`Tahsilat oranı %${summary.progress}`} />
        <SummaryCard label="İndirim" value={TL(sozlesme.indirim_tutar)} color="#F59E0B" hint={sozlesme.odeme_tipi ? `Ödeme tipi: ${sozlesme.odeme_tipi}` : null} />
      </div>

      <div className="card animate-fadeInUp" style={{ padding:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', gap:12, marginBottom:9 }}>
          <strong>Tahsilat ilerlemesi</strong>
          <strong style={{ color:summary.progress >= 100 ? '#10B981' : 'var(--blue)' }}>%{summary.progress}</strong>
        </div>
        <div style={{ height:10, borderRadius:99, overflow:'hidden', background:'var(--bg-elevated)' }}>
          <div style={{ width:`${summary.progress}%`, height:'100%', borderRadius:99, background:summary.progress >= 100 ? '#10B981' : 'var(--blue)', transition:'width .25s ease' }} />
        </div>
      </div>

      <div className="muh-grid-2">
        <div className="card" style={{ padding:18 }}>
          <div style={{ fontFamily:'var(--font-ui)', fontWeight:800, marginBottom:14 }}>👤 Öğrenci ve veli bilgileri</div>
          <div style={{ display:'grid', gridTemplateColumns:'minmax(110px, .7fr) 1.3fr', gap:'10px 14px', fontSize:'0.84rem' }}>
            {[
              ['Öğrenci', studentName],
              ['Öğrenci No', sozlesme.ogrenci_no],
              ['Sınıf', sozlesme.sinif_adi],
              ['Telefon', sozlesme.telefon],
              ['Veli', sozlesme.veli_adi],
              ['Veli Telefon', sozlesme.veli_tel],
              ['E-posta', sozlesme.veli_email || sozlesme.email],
            ].map(([label, value]) => (
              <React.Fragment key={label}>
                <span style={{ color:'var(--text-muted)' }}>{label}</span>
                <strong style={{ overflowWrap:'anywhere' }}>{value || '—'}</strong>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="card" style={{ padding:18 }}>
          <div style={{ fontFamily:'var(--font-ui)', fontWeight:800, marginBottom:14 }}>📋 Sözleşme bilgileri</div>
          <div style={{ display:'grid', gridTemplateColumns:'minmax(110px, .7fr) 1.3fr', gap:'10px 14px', fontSize:'0.84rem' }}>
            {[
              ['Paket', sozlesme.paket],
              ['Eğitim türü', sozlesme.egitim_turu],
              ['Eğitim yılı', sozlesme.egitim_yili],
              ['Başlangıç', formatDate(sozlesme.baslangic_tarihi)],
              ['Bitiş', formatDate(sozlesme.bitis_tarihi)],
              ['Taksit sayısı', summary.count],
              ['Notlar', sozlesme.notlar],
            ].map(([label, value]) => (
              <React.Fragment key={label}>
                <span style={{ color:'var(--text-muted)' }}>{label}</span>
                <strong style={{ overflowWrap:'anywhere' }}>{value || '—'}</strong>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div style={{ padding:'16px 18px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ fontFamily:'var(--font-ui)', fontWeight:850 }}>📅 Taksit planı ve bakiye detayları</div>
          <div style={{ color:'var(--text-muted)', fontSize:'0.76rem', marginTop:3 }}>Her taksidin planlanan, ödenen ve kalan tutarı</div>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Taksit</th>
                <th>Vade</th>
                <th>Planlanan</th>
                <th>Ödenen</th>
                <th>Kalan</th>
                <th>Son ödeme</th>
                <th>Yöntem</th>
                <th>Durum</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {(sozlesme.taksitler || []).map(item => {
                const status = STATUS[item.durum] || { label:item.durum, color:'#94A3B8' };
                return (
                  <tr key={item.id}>
                    <td style={{ fontWeight:850 }}>#{item.taksit_no}</td>
                    <td>{formatDate(item.vade_tarihi)}</td>
                    <td style={{ fontWeight:700 }}>{TL(item.tutar)}</td>
                    <td style={{ fontWeight:800, color:'#10B981' }}>{TL(item.odeme_tutari)}</td>
                    <td style={{ fontWeight:800, color:Number(item.kalan_tutar) > 0 ? '#EF4444' : '#10B981' }}>{TL(item.kalan_tutar)}</td>
                    <td>{formatDate(item.gercek_odeme)}</td>
                    <td>{paymentMethod(item.odeme_yontemi)}</td>
                    <td><span className="badge" style={{ background:`${status.color}22`, color:status.color }}>{status.label}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                        {(item.durum === 'odendi' || (item.durum === 'kismi' && Number(item.odeme_tutari) > 0)) && (
                          <button
                            className="btn btn-sm"
                            style={{ background:'#0284C7', color:'white', border:'none', fontWeight:700 }}
                            onClick={() => printInstallmentReceipt(item)}
                            title="Bu taksidin makbuzunu çıkart"
                          >
                            🧾 Makbuz
                          </button>
                        )}
                        {item.durum !== 'iptal' && remainingAmount(item) > 0 ? (
                          <button className="btn btn-sm" style={{ background:'#10B981', color:'white', border:'none' }}
                            onClick={() => setSelectedInstallment(item)}>
                            💳 Al
                          </button>
                        ) : null}
                        {!((item.durum === 'odendi' || (item.durum === 'kismi' && Number(item.odeme_tutari) > 0)) || (item.durum !== 'iptal' && remainingAmount(item) > 0)) && '—'}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div style={{ padding:'16px 18px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
          <div>
            <div style={{ fontFamily:'var(--font-ui)', fontWeight:850 }}>🧾 Tahsilat geçmişi</div>
            <div style={{ color:'var(--text-muted)', fontSize:'0.76rem', marginTop:3 }}>
              {studentName} · {collections.length} tahsilat kaydı
            </div>
          </div>
          {collections.length > 0 && (
            <button
              className="btn btn-sm btn-outline"
              style={{ color:'#0284C7', borderColor:'#38BDF8', fontWeight:700 }}
              onClick={printGeneralStatementReceipt}
            >
              🖨️ Toplu Makbuz / Ekstre
            </button>
          )}
        </div>
        {collections.length === 0 ? (
          <div className="empty-state" style={{ padding:34 }}><p>Henüz tahsilat kaydı bulunmuyor.</p></div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Taksit</th>
                  <th>Tutar</th>
                  <th>Yöntem</th>
                  <th>Makbuz / Fiş</th>
                  <th>Not</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {collections.map(payment => (
                  <tr key={payment.id}>
                    <td>{formatDate(payment.odeme_tarihi)}</td>
                    <td style={{ fontWeight:800 }}>{payment.taksitLabel}</td>
                    <td style={{ fontWeight:850, color:'#10B981' }}>{TL(payment.tutar)}</td>
                    <td>{paymentMethod(payment.odeme_yontemi)}</td>
                    <td>{payment.makbuz_no || '—'}</td>
                    <td style={{ maxWidth:280, whiteSpace:'pre-wrap' }}>{payment.notlar || '—'}</td>
                    <td>
                      <div style={{ display:'flex', gap:7, alignItems:'center', flexWrap:'wrap' }}>
                        <button
                          className="btn btn-sm"
                          style={{ background:'#0284C7', color:'white', border:'none', fontWeight:700 }}
                          onClick={() => printCollectionReceipt(payment)}
                          title="Bu tahsilatın makbuzunu çıkart"
                        >
                          🖨️ Makbuz
                        </button>
                        <button className="btn btn-outline btn-sm" onClick={() => setSelectedPayment(payment)}>
                          ✏️ Düzenle
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ background:'#FEF2F2', color:'#DC2626', border:'1px solid #FECACA' }}
                          onClick={() => deleteCollection(payment)}
                          disabled={deletingPaymentId === payment.id}
                        >
                          {deletingPaymentId === payment.id ? 'Siliniyor…' : '🗑️ Sil'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedInstallment && (
        <CollectionModal
          installment={selectedInstallment}
          installments={sozlesme.taksitler || []}
          studentName={studentName}
          onSave={saveCollection}
          onClose={() => setSelectedInstallment(null)}
        />
      )}
      {selectedPayment && (
        <EditCollectionModal
          payment={selectedPayment}
          studentName={studentName}
          onSave={updateCollection}
          onClose={() => setSelectedPayment(null)}
        />
      )}
    </div>
  );
}
